/**
 * WebSocket Hook for Real-Time Notifications
 * Provides connection management, auto-reconnection, and message handling
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Convert HTTP URL to WebSocket URL
const getWebSocketUrl = (organizationId) => {
  const baseUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
  return `${baseUrl}/ws/notifications/${organizationId}`;
};

export const useWebSocket = (organizationId, onMessage) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const onMessageRef = useRef(onMessage);
  const shouldReconnect = useRef(true);
  
  // Keep onMessage ref updated
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  
  const disconnect = useCallback(() => {
    shouldReconnect.current = false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);
  
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);
  
  // Main connection effect
  useEffect(() => {
    if (!organizationId) return;
    
    shouldReconnect.current = true;
    
    const createConnection = () => {
      // Clear any existing connection
      if (wsRef.current) {
        wsRef.current.close();
      }
      
      try {
        const wsUrl = getWebSocketUrl(organizationId);
        console.log('WebSocket connecting to:', wsUrl);
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
          console.log('WebSocket connected');
          setIsConnected(true);
          setConnectionError(null);
          reconnectAttempts.current = 0;
          
          // Start ping interval to keep connection alive
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, 30000);
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'pong') return;
            
            setLastMessage(data);
            
            if (onMessageRef.current && typeof onMessageRef.current === 'function') {
              onMessageRef.current(data);
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          setConnectionError('Connection error');
        };
        
        ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          setIsConnected(false);
          
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }
          
          // Attempt reconnection if not a clean close and reconnection is enabled
          if (shouldReconnect.current && event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
            console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttempts.current++;
              createConnection();
            }, delay);
          }
        };
      } catch (error) {
        console.error('Error creating WebSocket:', error);
        setConnectionError(error.message);
      }
    };
    
    createConnection();
    
    // Cleanup on unmount or when organizationId changes
    return () => {
      shouldReconnect.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Cleanup');
        wsRef.current = null;
      }
    };
  }, [organizationId]);
  
  const reconnect = useCallback(() => {
    shouldReconnect.current = true;
    reconnectAttempts.current = 0;
    // Force reconnection by closing existing connection
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual reconnect');
    }
  }, []);
  
  return {
    isConnected,
    lastMessage,
    connectionError,
    sendMessage,
    reconnect,
    disconnect
  };
};

export default useWebSocket;
